/**
 * IncassoDossierDetailPage
 *
 * Full-page replacement for the DossierDetailSheet drawer. Matches the
 * helpdesk detail page in structure: sticky header, 2-column grid for
 * emails + ticket details, WhatsApp section in row-2 column-2, then
 * dossier-specific content (debt chart, invoices, quick actions) below.
 *
 * The "Reageer" / new-email flow routes to /mass-mail with the existing
 * incasso context (same handlers as the old drawer) — and `replyToEmail`
 * + `autoSelectFirstTemplate: false` when replying so the user lands on
 * a clean WYSIWYG with the quoted message.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession, useUser } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { Spinner, Button, Chip } from '@heroui/react';
import {
  ArrowLeft,
  Building,
  Cube,
  Phone,
  EnvelopeSimple,
  ClipboardText,
  Scales,
  ListChecks,
  BellSimple,
  Eye,
  DownloadSimple,
  Warning,
  CalendarBlank,
  Invoice,
  ChatText,
  PencilSimple,
  Plus,
} from '@phosphor-icons/react';
import { Badge } from 'components/ui/badge';
import { Textarea } from 'components/ui/textarea';
import { cn } from '@/lib/utils';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { applyMask } from 'shared/utils/masks';
import { TwilioCallButton } from 'shared/components/TwilioCallButton/TwilioCallButton';
import { DevWhatsAppButton } from 'shared/components/TwilioCallButton/DevWhatsAppButton';
import { WhatsAppContactSection } from 'shared/components/whatsapp/WhatsAppContactSection';
import {
  NotesEditModal,
  IncassoActionModal,
  LegalProcedureModal,
  PaymentPlanModal,
} from 'shared/components/rental/sections/incasso/components';
import { CustomerInvoice } from 'shared/types/customer-invoice';
import { INCASSO_EMAIL_TEMPLATES } from '../shared/incassoTemplates';
import { IncassoTicket, IncassoBeoordeling } from '../shared/incassoTypes';
import { getPhaseLabel } from '../configs/incassoPhaseConfig';
import { BeoordelingPopover } from '../components/BeoordelingPopover';
import { SnoozeIndicator } from '../components/SnoozeIndicator';
import { TenantDebtChart } from '../components/TenantDebtChart';
import { SnoozeDialog } from '../components/SnoozeDialog';
import { IncassoEmailsSection, IncassoEmail } from '../components/IncassoEmailsSection';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InvoiceDetail {
  id: number;
  name: string;
  amount_total: number;
  amount_residual: number;
  amount_paid: number;
  invoice_date: string;
  invoice_date_due: string;
  days_overdue: number;
  state: string;
  has_attachment: boolean;
  company_id: number | null;
}

interface InvoiceDetailsData {
  invoices: InvoiceDetail[];
  totals: {
    total_amount: number;
    total_residual: number;
    total_paid: number;
    invoice_count: number;
  };
}

const BEOORDELING_LABELS: Record<IncassoBeoordeling, string> = {
  [IncassoBeoordeling.KOMT_IN_ORDE]: 'Komt wellicht in orde',
  [IncassoBeoordeling.NIET_BEREIKBAAR]: 'Niet bereikbaar',
  [IncassoBeoordeling.KOMT_NIET_IN_ORDE]: 'Komt niet in orde',
};

const BEOORDELING_COLORS: Record<IncassoBeoordeling, string> = {
  [IncassoBeoordeling.KOMT_IN_ORDE]: 'bg-green-100 text-green-800',
  [IncassoBeoordeling.NIET_BEREIKBAAR]: 'bg-amber-100 text-amber-800',
  [IncassoBeoordeling.KOMT_NIET_IN_ORDE]: 'bg-red-100 text-red-800',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('nl-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
};

/** Convert IncassoTicket to CustomerInvoice shape for the legacy modals. */
const ticketToInvoice = (ticket: IncassoTicket | null): CustomerInvoice | null => {
  if (!ticket) return null;
  return {
    id: Number(ticket.id),
    name: '',
    amount_residual: Number(ticket.cachedTotalDebt) || 0,
    contact: ticket.contact
      ? {
          id: ticket.contact.id,
          firstName: ticket.contact.firstName,
          lastName: ticket.contact.lastName,
          phone_mobile: ticket.contact.phone_mobile || undefined,
          phone_direct: ticket.contact.phone_direct || undefined,
          notes: ticket.contact.notes,
          email: ticket.contact.email || '',
          jobTitle: '',
        }
      : null,
    company: ticket.company
      ? {
          id: ticket.company.id,
          name: ticket.company.name,
          phone_direct: ticket.company.phone_direct,
          notes: ticket.company.notes,
        }
      : null,
    unit: null,
    partner_id: [ticket.odooPartnerId, ticket.odooPartnerDisplayName],
    invoice_partner_display_name: ticket.odooPartnerDisplayName,
    x_studio_sub_unit: '',
    invoice_date: null,
    invoice_date_due: null,
    invoice_days_due: String(ticket.cachedMaxDaysOverdue || 0),
    analytic_account: ticket.cachedBuildingName || '',
    incassoActionCount: ticket.incassoActionCount || 0,
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

export const IncassoDossierDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const { user } = useUser();

  const [ticket, setTicket] = useState<IncassoTicket | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Invoice state
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsData | null>(null);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState<Record<string, boolean>>({});
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Notes state
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Modal state — these used to live in the parent list/kanban page; now the
  // detail page owns them so the user can trigger them from the quick-action
  // buttons without leaving the page.
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [paymentPlanModalOpen, setPaymentPlanModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  // ─── Fetch ticket ──────────────────────────────────────────────────────────
  const fetchTicket = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/${id}`,
        { method: 'GET' },
        session,
      );
      if (!response.ok) throw new Error(`Dossier ophalen mislukt (${response.status})`);
      const data: IncassoTicket = await response.json();
      setTicket(data);
      setNotesValue(data.notes ?? '');
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'incasso-detail', op: 'fetch-ticket' },
        extra: { ticketId: id },
      });
      console.error('[IncassoDossierDetailPage] fetchTicket error:', error);
      toast.error('Fout bij laden van dossier');
    } finally {
      setIsLoading(false);
    }
  }, [id, session]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  // ─── Fetch invoices once ticket is loaded ─────────────────────────────────
  useEffect(() => {
    if (!ticket?.odooPartnerId) return;
    const partnerId = ticket.odooPartnerId;
    setIsLoadingInvoices(true);
    setInvoiceDetails(null);
    fetchWithAuth(
      `${API_URL}/rental/contracts/customer-invoices/${partnerId}`,
      { method: 'GET' },
      session,
    )
      .then(async (response) => {
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        const data = await response.json();
        setInvoiceDetails(data);
      })
      .catch((error) => {
        console.error('[IncassoDossierDetailPage] Error fetching invoices:', error);
        toast.error('Fout bij het ophalen van factuurdetails');
      })
      .finally(() => setIsLoadingInvoices(false));
  }, [ticket?.odooPartnerId, session]);

  // ─── PDF actions ──────────────────────────────────────────────────────────
  const handleViewPdf = useCallback(
    async (invoiceName: string, companyId?: number | null) => {
      setLoadingPdf((prev) => ({ ...prev, [invoiceName]: true }));
      try {
        const companyParam = companyId ? `?companyId=${companyId}` : '';
        const response = await fetchWithAuth(
          `${API_URL}/rental/contracts/invoice-pdf/${encodeURIComponent(invoiceName)}${companyParam}`,
          { method: 'GET' },
          session,
        );
        const text = await response.text();
        const data = text ? JSON.parse(text) : null;
        if (data?.data) {
          const pdfBlob = base64ToBlob(data.data, 'application/pdf');
          window.open(URL.createObjectURL(pdfBlob), '_blank');
        } else {
          toast.error('Geen PDF beschikbaar voor deze factuur');
        }
      } catch (error) {
        console.error('[IncassoDossierDetailPage] Error fetching PDF:', error);
        toast.error('Fout bij het ophalen van de factuur PDF');
      } finally {
        setLoadingPdf((prev) => ({ ...prev, [invoiceName]: false }));
      }
    },
    [session],
  );

  const handleDownloadAll = useCallback(async () => {
    if (!invoiceDetails?.invoices.length) return;
    setDownloadingAll(true);
    try {
      const invoices = invoiceDetails.invoices.map((inv) => ({
        name: inv.name,
        companyId: inv.company_id ?? undefined,
      }));
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/invoice-pdfs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoices }),
        },
        session,
      );
      const results = await response.json();
      let downloadCount = 0;
      for (const result of results) {
        if (result.pdf?.data) {
          const pdfBlob = base64ToBlob(result.pdf.data, 'application/pdf');
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = result.pdf.name || `${result.invoiceName}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          downloadCount++;
        }
      }
      if (downloadCount > 0) toast.success(`${downloadCount} facturen gedownload`);
      else toast.warning('Geen facturen beschikbaar om te downloaden');
    } catch (error) {
      console.error('[IncassoDossierDetailPage] Error downloading invoices:', error);
      toast.error('Fout bij het downloaden van facturen');
    } finally {
      setDownloadingAll(false);
    }
  }, [invoiceDetails, session]);

  // ─── Notes ────────────────────────────────────────────────────────────────
  const handleSaveNotes = useCallback(async () => {
    if (!ticket) return;
    setIsSavingNotes(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/${ticket.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes: notesValue }),
        },
        session,
      );
      if (!response.ok) throw new Error('Notities opslaan mislukt');
      toast.success('Notities opgeslagen');
      setEditingNotes(false);
      setTicket((prev) => (prev ? { ...prev, notes: notesValue } : prev));
    } catch (error) {
      console.error('[IncassoDossierDetailPage] Error saving notes:', error);
      toast.error('Fout bij opslaan van notities');
    } finally {
      setIsSavingNotes(false);
    }
  }, [ticket, notesValue, session]);

  // ─── Beoordeling change ───────────────────────────────────────────────────
  const handleBeoordelingChange = useCallback(
    async (beoordeling: IncassoBeoordeling | null, reden?: string) => {
      if (!ticket) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-tickets/${ticket.id}/beoordeling`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ beoordeling, beoordelingReden: reden ?? null }),
          },
          session,
        );
        if (!response.ok) throw new Error('Beoordeling opslaan mislukt');
        setTicket((prev) =>
          prev ? { ...prev, beoordeling, beoordelingReden: reden ?? null } : prev,
        );
      } catch (error) {
        console.error('[IncassoDossierDetailPage] Error saving beoordeling:', error);
        toast.error('Fout bij opslaan van beoordeling');
      }
    },
    [ticket, session],
  );

  // ─── Snooze ───────────────────────────────────────────────────────────────
  const handleSnooze = useCallback(
    async (snoozeUntil: string, reason: string) => {
      if (!ticket) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-tickets/${ticket.id}/snooze`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ snoozeUntil, snoozeReason: reason }),
          },
          session,
        );
        if (!response.ok) throw new Error('Snooze opslaan mislukt');
        await fetchTicket();
        toast.success('Dossier gesnoozed');
      } catch (error) {
        console.error('[IncassoDossierDetailPage] Error snoozing:', error);
        toast.error('Fout bij snoozen');
      }
    },
    [ticket, session, fetchTicket],
  );

  const handleUnsnooze = useCallback(async () => {
    if (!ticket) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/${ticket.id}/unsnooze`,
        { method: 'PUT' },
        session,
      );
      if (!response.ok) throw new Error('Snooze opheffen mislukt');
      await fetchTicket();
      toast.success('Snooze opgeheven');
    } catch (error) {
      console.error('[IncassoDossierDetailPage] Error unsnoozing:', error);
      toast.error('Fout bij opheffen van snooze');
    }
  }, [ticket, session, fetchTicket]);

  // ─── Log phone call from Twilio button ────────────────────────────────────
  const handleLogPhoneCall = useCallback(
    async (phoneNumber: string, durationSeconds?: number) => {
      if (!ticket || !user?.id) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-actions/log-phone-call`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              odooPartnerId: ticket.odooPartnerId,
              phoneNumber,
              createdByUserId: user.id,
              contactId: ticket.contact?.id,
              companyId: ticket.company?.id,
              durationSeconds,
            }),
          },
          session,
        );
        if (response.ok) {
          setTicket((prev) =>
            prev
              ? { ...prev, incassoActionCount: (prev.incassoActionCount || 0) + 1 }
              : prev,
          );
          toast.success('Telefoongesprek gelogd');
        }
      } catch (error) {
        console.error('[IncassoDossierDetailPage] Error logging phone call:', error);
      }
    },
    [ticket, user, session],
  );

  // ─── Navigate to MassMailPage (new email or reply) ────────────────────────
  // Mirrors the old DossierDetailSheet.handleNavigateToMassmail — including
  // the reply-with-quote path we wired in the previous task.
  const handleNavigateToMassmail = useCallback(
    (subject?: string, content?: string, replyToEmail?: IncassoEmail) => {
      if (!ticket) return;
      try {
        const email = ticket.contact?.email || ticket.company?.email || '';
        const firstName = ticket.contact?.firstName || ticket.company?.name || '';
        const lastName = ticket.contact?.lastName || '';
        const contactName = ticket.contact
          ? `${firstName} ${lastName}`.trim()
          : ticket.company?.name || ticket.odooPartnerDisplayName;

        const contact = {
          id:
            ticket.contact?.id?.toString() ||
            ticket.company?.id?.toString() ||
            `incasso-${ticket.id}`,
          name: contactName || 'Onbekend contact',
          firstName,
          lastName,
          email,
          resourceId:
            ticket.contact?.id?.toString() || ticket.company?.id?.toString() || '',
          units: [],
          buildingId: '',
          buildingName: ticket.cachedBuildingName || '',
          unitId: '',
          unitNumber: '',
        };

        const totalDebtNumber = Number(ticket.cachedTotalDebt) || 0;
        const formattedTotalDebt = new Intl.NumberFormat('nl-BE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(totalDebtNumber);

        // Let MassMail fetch the same Odoo invoice PDFs shown in the dossier
        // invoice table and add them to the outgoing mail as attachments.
        const pendingInvoiceAttachments = (invoiceDetails?.invoices ?? []).map((invoice) => ({
          name: invoice.name,
          companyId: invoice.company_id ?? null,
        }));

        navigate('/mass-mail', {
          state: {
            contacts: [contact],
            emailSubject:
              subject || `Betreft uw openstaande facturen [INC-${Number(ticket.id)}]`,
            emailContent: content || '',
            senderEmail: 'incasso@yally.be',
            contextTemplates: INCASSO_EMAIL_TEMPLATES,
            contextPlaceholders: {
              ticketId: `INC-${Number(ticket.id)}`,
              partnerName: ticket.odooPartnerDisplayName,
              buildingName: ticket.cachedBuildingName || '',
              totalDebt: formattedTotalDebt,
            },
            pendingInvoiceAttachments,
            ...(replyToEmail
              ? { replyToEmail, autoSelectFirstTemplate: false }
              : {}),
          },
        });
      } catch (error) {
        console.error('[IncassoDossierDetailPage] Error navigating to mass mail:', error);
        toast.error('Fout bij navigeren naar e-mail pagina');
      }
    },
    [ticket, invoiceDetails, navigate],
  );

  const handleNewEmail = useCallback(() => {
    handleNavigateToMassmail();
  }, [handleNavigateToMassmail]);

  const handleReplyEmail = useCallback(
    (email: IncassoEmail) => {
      const replySubject = email.subject.startsWith('Re:')
        ? email.subject
        : `Re: ${email.subject}`;
      handleNavigateToMassmail(replySubject, undefined, email);
    },
    [handleNavigateToMassmail],
  );

  // ─── Derived display values ────────────────────────────────────────────────
  const isSnoozed = useMemo(
    () =>
      ticket
        ? (ticket.isSnoozed ??
          (ticket.snoozeUntil ? new Date(ticket.snoozeUntil) > new Date() : false))
        : false,
    [ticket],
  );
  const phoneNumber =
    ticket?.contact?.phone_mobile ||
    ticket?.contact?.phone_direct ||
    ticket?.company?.phone_direct;
  const contactEmail = ticket?.contact?.email || ticket?.company?.email;
  const contactName = ticket?.contact
    ? `${ticket.contact.firstName} ${ticket.contact.lastName}`
    : ticket?.company?.name ?? ticket?.odooPartnerDisplayName;

  // ─── Render ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="text-neutral-secondary-mid mt-4">Dossier laden...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-4xl">❌</div>
          <p className="mb-2 font-medium">Dossier niet gevonden</p>
          <Button onClick={() => navigate('/rental/incasso/list')} className="mt-4">
            Terug naar dossiers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="sticky-below-header bleed-full-width bg-muted mb-6 py-3 shadow-sm sm:py-6">
        <div className="mb-4 flex items-start justify-between">
          {/* Left: back + name + chips */}
          <div className="flex flex-1 items-start gap-4">
            <Button
              isIconOnly
              variant="light"
              onClick={() => navigate('/rental/incasso/list')}
              className="text-neutral-secondary-mid hover:text-brick-primary mt-1"
              aria-label="Terug"
            >
              <ArrowLeft size={20} />
            </Button>
            <div className="flex-1">
              <div className="mb-3 flex items-center gap-3 flex-wrap">
                <div className="bg-brick-primary h-8 w-3 rounded-full"></div>
                <div className="text-neutral-primary text-2xl font-bold">
                  INC-{Number(ticket.id)}
                </div>
                <h1 className="text-neutral-primary text-lg font-semibold">
                  {ticket.odooPartnerDisplayName}
                </h1>
                <Badge variant="outline" className="text-xs font-medium">
                  {getPhaseLabel(ticket.phase)}
                </Badge>
              </div>

              {/* Metadata chips */}
              <div className="flex flex-wrap items-center gap-2">
                {ticket.cachedBuildingName && (
                  <Chip
                    size="sm"
                    className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border"
                    startContent={<Building size={12} weight="duotone" />}
                  >
                    {ticket.cachedBuildingName}
                  </Chip>
                )}
                {ticket.cachedUnitLabel && (
                  <Chip
                    size="sm"
                    className="bg-blue-50 text-blue-700 border-blue-200 border"
                    startContent={<Cube size={12} weight="duotone" />}
                  >
                    {ticket.cachedUnitLabel}
                  </Chip>
                )}
                {ticket.cachedCompanyName && (
                  <Chip
                    size="sm"
                    className="bg-neutral-100 text-neutral-700 border border-neutral-200"
                  >
                    {ticket.cachedCompanyName}
                  </Chip>
                )}
                <Chip
                  size="sm"
                  className="bg-red-50 text-red-700 border border-red-200 font-bold"
                >
                  {applyMask(Number(ticket.cachedTotalDebt) || 0, 'valuta')}
                </Chip>
                <Chip
                  size="sm"
                  className="border border-neutral-200 bg-white text-neutral-secondary-mid"
                >
                  {ticket.cachedInvoiceCount || 0} facturen
                </Chip>
                <Chip
                  size="sm"
                  className={cn(
                    'border',
                    (ticket.cachedMaxDaysOverdue ?? 0) >= 90
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : (ticket.cachedMaxDaysOverdue ?? 0) >= 30
                        ? 'border-orange-200 bg-orange-50 text-orange-700'
                        : 'border-neutral-200 bg-white text-neutral-secondary-mid',
                  )}
                >
                  {ticket.cachedMaxDaysOverdue ?? 0}d te laat
                </Chip>
                {/* Opzichmogelijkheden: wanneer er bezichtigingen mogen doorgaan en wie
                    ze doet. Staat al op het huurcontract en wordt bij een opzeg
                    ingevuld — juist bij een incassodossier wil je dat erbij zien. */}
                {ticket.visitation && (
                  <Chip
                    size="sm"
                    className={cn(
                      'border',
                      ticket.visitation.days
                        ? 'border-moss-primary/30 bg-moss-primary/10 text-moss-primary'
                        : 'border-neutral-200 bg-white text-neutral-secondary-mid',
                    )}
                    startContent={<Eye size={12} weight="duotone" />}
                  >
                    {ticket.visitation.days
                      ? `Opzicht: ${ticket.visitation.days}`
                      : 'Opzicht: niet ingevuld'}
                    {ticket.visitation.responsible &&
                      ticket.visitation.responsible !== 'Onbekend' &&
                      ` (${ticket.visitation.responsible})`}
                  </Chip>
                )}
              </div>

              {/* Beoordeling row */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <BeoordelingPopover
                  value={ticket.beoordeling}
                  reden={ticket.beoordelingReden}
                  onChange={(v, reden) => handleBeoordelingChange(v, reden)}
                />
                {ticket.beoordeling && (
                  <span
                    className={cn(
                      'text-xs font-medium px-2 py-0.5 rounded-full',
                      BEOORDELING_COLORS[ticket.beoordeling],
                    )}
                  >
                    {BEOORDELING_LABELS[ticket.beoordeling]}
                  </span>
                )}
                {ticket.beoordelingReden && (
                  <span className="text-xs text-neutral-secondary-mid italic truncate max-w-[400px]">
                    — {ticket.beoordelingReden}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: top-right action area — Twilio call + WhatsApp + new email */}
          <div className="flex items-center gap-3">
            {phoneNumber && (
              <div className="border-neutral-primary/20 flex items-center gap-2 rounded-lg border bg-white/80 px-3 py-2">
                <Phone size={14} className="text-neutral-secondary-mid" />
                <span className="text-sm text-neutral-secondary-mid">{phoneNumber}</span>
                <TwilioCallButton
                  phoneNumber={phoneNumber}
                  contactName={ticket.odooPartnerDisplayName}
                  size={16}
                  onCallCompleted={(ph, dur) => handleLogPhoneCall(ph, dur)}
                />
                <DevWhatsAppButton
                  phoneNumber={phoneNumber}
                  contactId={ticket.contact?.id}
                  companyId={ticket.company?.id}
                  contactName={ticket.odooPartnerDisplayName}
                  size={16}
                />
              </div>
            )}
            <Button
              onClick={handleNewEmail}
              className="bg-moss-primary hover:bg-moss-primary/90 text-white"
              startContent={<Plus size={20} />}
            >
              Verstuur nieuwe email
            </Button>
          </div>
        </div>

        {/* Snooze indicator banner */}
        {isSnoozed && ticket.snoozeUntil && (
          <div className="mt-2">
            <SnoozeIndicator
              snoozeUntil={ticket.snoozeUntil}
              snoozeReason={ticket.snoozeReason}
            />
          </div>
        )}
      </div>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-full px-3 pb-6 sm:px-6">
        {/* Two-column grid — emails + dossier details (row 1), WhatsApp (row 2) */}
        <div className="mb-3 grid grid-cols-1 gap-3 sm:mb-6 sm:gap-6 lg:grid-cols-2">
          {/* IncassoEmailsSection renders two siblings (table + detail panel)
              just like the helpdesk EmailsSection — they fill row 1 of the grid. */}
          <IncassoEmailsSection
            ticket={ticket}
            onNewEmail={handleNewEmail}
            onReply={handleReplyEmail}
          />

          {/* Dossier Details — row 2 col 1 */}
          <div className="bg-muted border-muted-foreground/20 overflow-hidden rounded-lg border">
            <div className="border-neutral-primary/10 border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <PencilSimple
                  size={18}
                  className="text-moss-primary"
                  weight="duotone"
                />
                <h3 className="text-neutral-primary text-base font-semibold">
                  Dossier Details
                </h3>
              </div>
            </div>
            <div className="divide-y divide-neutral-primary/10">
              {/* Contact */}
              <div className="px-4 py-3 space-y-1">
                <p className="text-xs font-medium text-neutral-secondary-mid uppercase">
                  Contact
                </p>
                <p className="text-sm font-medium text-neutral-primary">
                  {contactName}
                </p>
                {contactEmail && (
                  <a
                    href={`mailto:${contactEmail}`}
                    className="flex items-center gap-1 text-sm text-neutral-secondary-mid hover:text-moss-primary"
                  >
                    <EnvelopeSimple size={14} />
                    {contactEmail}
                  </a>
                )}
                {(ticket.contact?.notes || ticket.company?.notes) && (
                  <div className="flex items-start gap-1 text-xs text-neutral-secondary-mid pt-1">
                    <ChatText size={14} className="shrink-0 mt-0.5" />
                    <span className="italic">
                      {ticket.contact?.notes || ticket.company?.notes}
                    </span>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="px-4 py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-neutral-secondary-mid uppercase">
                    Dossier notities
                  </p>
                  {!editingNotes ? (
                    <button
                      onClick={() => setEditingNotes(true)}
                      className="text-xs text-moss-primary hover:underline"
                    >
                      Bewerken
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingNotes(false);
                          setNotesValue(ticket.notes ?? '');
                        }}
                        className="text-xs text-neutral-secondary-mid hover:underline"
                      >
                        Annuleren
                      </button>
                      <button
                        onClick={handleSaveNotes}
                        disabled={isSavingNotes}
                        className="text-xs text-moss-primary hover:underline font-medium disabled:opacity-50"
                      >
                        {isSavingNotes ? 'Opslaan...' : 'Opslaan'}
                      </button>
                    </div>
                  )}
                </div>
                {editingNotes ? (
                  <Textarea
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    placeholder="Notities over dit dossier..."
                    className="min-h-[80px] text-sm"
                  />
                ) : (
                  <p className="text-sm text-neutral-secondary-mid whitespace-pre-wrap">
                    {notesValue || <span className="italic">Geen notities</span>}
                  </p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="px-4 py-3 space-y-2">
                <p className="text-xs font-medium text-neutral-secondary-mid uppercase">
                  Acties
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSnoozeOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <BellSimple weight="duotone" size={16} />
                    {isSnoozed ? 'Snooze bewerken' : 'Snooze instellen'}
                  </button>
                  {isSnoozed && (
                    <button
                      onClick={handleUnsnooze}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      Snooze opheffen
                    </button>
                  )}
                  <button
                    onClick={() => setActionModalOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <ClipboardText weight="duotone" size={16} />
                    Incasso acties
                    {(ticket.incassoActionCount ?? 0) > 0 && (
                      <span className="text-xs text-moss-primary font-medium">
                        ({ticket.incassoActionCount})
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setLegalModalOpen(true)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-colors',
                      (ticket.activeLegalProcedures ?? 0) > 0
                        ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                        : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50',
                    )}
                  >
                    <Scales weight="duotone" size={16} />
                    Juridische procedures
                    {(ticket.legalProcedureCount ?? 0) > 0 && (
                      <span className="text-xs font-medium">
                        ({ticket.legalProcedureCount})
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setPaymentPlanModalOpen(true)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-colors',
                      (ticket.activePaymentPlans ?? 0) > 0
                        ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                        : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50',
                    )}
                  >
                    <ListChecks weight="duotone" size={16} />
                    Afbetalingsplannen
                    {ticket.paymentPlanProgress && (
                      <span className="text-xs font-medium">
                        ({ticket.paymentPlanProgress})
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setIsNotesModalOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <ChatText weight="duotone" size={16} />
                    Contactnotities
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp Section — same grid position as helpdesk (row 2 col 2) */}
          {ticket.contact?.id && (
            <WhatsAppContactSection
              contactId={ticket.contact.id}
              contactName={contactName || 'Onbekend contact'}
              hasMobile={!!ticket.contact.phone_mobile}
              daysFilter={30}
            />
          )}
        </div>

        {/* Debt evolution chart */}
        <div className="mb-6 rounded-lg border border-muted-foreground/20 bg-muted p-4">
          <TenantDebtChart
            odooPartnerId={ticket.odooPartnerId}
            tenantName={ticket.odooPartnerDisplayName}
          />
        </div>

        {/* Invoices */}
        <div className="rounded-lg border border-muted-foreground/20 bg-muted overflow-hidden">
          <div className="border-b border-neutral-primary/10 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Invoice size={18} className="text-moss-primary" weight="duotone" />
              <h3 className="text-neutral-primary text-base font-semibold">Facturen</h3>
            </div>
            {invoiceDetails && invoiceDetails.invoices.length > 0 && (
              <button
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                className="flex items-center gap-1 text-xs text-moss-primary hover:underline disabled:opacity-50"
              >
                <DownloadSimple size={14} />
                {downloadingAll ? 'Downloaden...' : 'Download alle facturen'}
              </button>
            )}
          </div>

          <div className="p-4">
            {isLoadingInvoices ? (
              <div className="flex items-center justify-center py-6 text-sm text-neutral-secondary-mid">
                Factuurdetails ophalen...
              </div>
            ) : invoiceDetails ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  <div className="bg-white rounded-lg p-2 border">
                    <p className="text-[11px] text-neutral-secondary-mid">
                      Totaal gefactureerd
                    </p>
                    <p className="text-sm font-semibold">
                      {applyMask(invoiceDetails.totals.total_amount, 'valuta')}
                    </p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2 border border-red-100">
                    <p className="text-[11px] text-neutral-secondary-mid">Openstaand</p>
                    <p className="text-sm font-semibold text-red-700">
                      {applyMask(invoiceDetails.totals.total_residual, 'valuta')}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2 border border-green-100">
                    <p className="text-[11px] text-neutral-secondary-mid">Betaald</p>
                    <p className="text-sm font-semibold text-green-700">
                      {applyMask(invoiceDetails.totals.total_paid, 'valuta')}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-2 border">
                    <p className="text-[11px] text-neutral-secondary-mid">Aantal</p>
                    <p className="text-sm font-semibold">
                      {invoiceDetails.totals.invoice_count}
                    </p>
                  </div>
                </div>

                {invoiceDetails.invoices.length > 0 && (
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="text-left p-2 font-medium">Factuur</th>
                          <th className="text-right p-2 font-medium">Openstaand</th>
                          <th className="text-center p-2 font-medium">Te laat</th>
                          <th className="text-center p-2 font-medium">Vervaldatum</th>
                          <th className="text-right p-2 font-medium">PDF</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {invoiceDetails.invoices.map((inv) => (
                          <tr key={inv.id} className="hover:bg-neutral-50/50">
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <Invoice
                                  weight="duotone"
                                  size={14}
                                  className="text-neutral-secondary-mid"
                                />
                                <span className="font-medium">{inv.name}</span>
                              </div>
                            </td>
                            <td className="p-2 text-right text-red-700 font-medium">
                              {applyMask(inv.amount_residual, 'valuta')}
                            </td>
                            <td className="p-2 text-center">
                              <span
                                className={cn(
                                  'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium',
                                  inv.days_overdue > 90
                                    ? 'bg-red-100 text-red-700'
                                    : inv.days_overdue > 30
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-neutral-100 text-neutral-secondary-mid',
                                )}
                              >
                                {inv.days_overdue > 90 && (
                                  <Warning weight="fill" size={10} />
                                )}
                                {inv.days_overdue}d
                              </span>
                            </td>
                            <td className="p-2 text-center text-neutral-secondary-mid">
                              <span className="inline-flex items-center gap-0.5">
                                <CalendarBlank size={12} />
                                {formatDate(inv.invoice_date_due)}
                              </span>
                            </td>
                            <td className="p-2 text-right">
                              <button
                                onClick={() => handleViewPdf(inv.name, inv.company_id)}
                                disabled={loadingPdf[inv.name]}
                                className="p-1 rounded hover:bg-moss-primary/10 text-moss-primary transition-colors disabled:opacity-50"
                                title="Bekijk factuur"
                              >
                                {loadingPdf[inv.name] ? (
                                  <span className="text-[10px]">...</span>
                                ) : (
                                  <Eye weight="duotone" size={16} />
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-neutral-secondary-mid italic">
                Geen factuurdetails beschikbaar
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      <SnoozeDialog
        isOpen={snoozeOpen}
        onClose={() => setSnoozeOpen(false)}
        ticketLabel={ticket.odooPartnerDisplayName}
        currentSnooze={
          isSnoozed && ticket.snoozeUntil
            ? { until: ticket.snoozeUntil, reason: ticket.snoozeReason ?? '' }
            : null
        }
        onConfirm={(snoozeUntil, reason) => {
          handleSnooze(snoozeUntil, reason);
        }}
        onUnsnooze={handleUnsnooze}
      />

      <NotesEditModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        invoice={ticketToInvoice(ticket)}
        onNotesUpdated={(invoice, notes) => {
          setTicket((prev) => {
            if (!prev) return prev;
            if (prev.contact && invoice.contact) {
              return { ...prev, contact: { ...prev.contact, notes } };
            }
            if (prev.company && invoice.company) {
              return { ...prev, company: { ...prev.company, notes } };
            }
            return prev;
          });
        }}
      />

      <IncassoActionModal
        isOpen={actionModalOpen}
        onClose={() => {
          setActionModalOpen(false);
          fetchTicket();
        }}
        invoice={ticketToInvoice(ticket)}
        onActionCreated={() => {
          setTicket((prev) =>
            prev
              ? { ...prev, incassoActionCount: (prev.incassoActionCount || 0) + 1 }
              : prev,
          );
        }}
      />

      <LegalProcedureModal
        isOpen={legalModalOpen}
        onClose={() => {
          setLegalModalOpen(false);
          fetchTicket();
        }}
        invoice={ticketToInvoice(ticket)}
      />

      <PaymentPlanModal
        isOpen={paymentPlanModalOpen}
        onClose={() => {
          setPaymentPlanModalOpen(false);
          fetchTicket();
        }}
        invoice={ticketToInvoice(ticket)}
      />
    </div>
  );
};
